
import { Card, CardContent } from "@/components/ui/card";
import { FileText, MessageSquare, CheckCircle, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ConversationTranscriptViewProps {
  conversationDetails: Record<string, any> | null;
}

const ConversationTranscriptView = ({ conversationDetails }: ConversationTranscriptViewProps) => {
  if (!conversationDetails) {
    return (
      <div className="text-center py-6">
        <p className="text-muted-foreground">No conversation details available</p>
      </div>
    );
  }

  const hasTranscript = conversationDetails.transcript_summary || conversationDetails.reactivated_rationale;
  const outcome = conversationDetails.outcome?.toLowerCase();
  const isReactivated = outcome === 'reactivated' || outcome === 'callback_requested';

  if (!hasTranscript) {
    return (
      <div className="text-center py-6">
        <p className="text-muted-foreground">No transcript or summary available for this conversation</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Call Outcome Summary */}
      <Card>
        <div className="bg-accent/50 px-6 py-3 flex items-center">
          {isReactivated ? <CheckCircle className="h-5 w-5 text-green-500" /> : <XCircle className="h-5 w-5 text-red-500" />}
          <h3 className="text-sm font-medium ml-2">Reactivation Status</h3>
        </div>
        <CardContent className="pt-4">
          <div className="flex justify-between items-center mb-3">
            <div>
              <Badge variant={isReactivated ? "success" : "destructive"}>
                {outcome || "Unknown"}
              </Badge>
              {conversationDetails.booking_made && (
                <Badge variant="outline" className="ml-2">Booking Made</Badge>
              )}
            </div>
          </div>
          
          {conversationDetails.reactivated_rationale && (
            <div className="bg-accent/20 p-4 rounded-md border">
              <h4 className="text-sm font-medium mb-2">Reactivation Rationale:</h4>
              <p className="text-sm whitespace-pre-wrap">
                {conversationDetails.reactivated_rationale}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversation Summary */}
      {conversationDetails.transcript_summary && (
        <Card>
          <div className="bg-accent/50 px-6 py-3 flex items-center">
            <MessageSquare className="h-4 w-4" />
            <h3 className="text-sm font-medium ml-2">Conversation Summary</h3>
          </div>
          <CardContent className="pt-4">
            <div className="bg-accent/20 p-4 rounded-md border">
              <p className="text-sm whitespace-pre-wrap">
                {conversationDetails.transcript_summary}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metadata - if it contains transcript */}
      {conversationDetails.metadata_json && typeof conversationDetails.metadata_json === 'object' && 
       conversationDetails.metadata_json.transcript && (
        <Card>
          <div className="bg-accent/50 px-6 py-3 flex items-center">
            <FileText className="h-4 w-4" />
            <h3 className="text-sm font-medium ml-2">Full Transcript</h3>
          </div>
          <CardContent className="pt-4">
            <div className="bg-accent/20 p-4 rounded-md border max-h-96 overflow-y-auto">
              <pre className="text-sm whitespace-pre-wrap">
                {Array.isArray(conversationDetails.metadata_json.transcript) 
                  ? conversationDetails.metadata_json.transcript.join('\n\n')
                  : typeof conversationDetails.metadata_json.transcript === 'string'
                    ? conversationDetails.metadata_json.transcript
                    : JSON.stringify(conversationDetails.metadata_json.transcript, null, 2)
                }
              </pre>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ConversationTranscriptView;
